/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from 'react';
import { Box, Stack, Tab, Tabs, Tooltip, Typography, useTheme, Alert, AlertTitle } from '@mui/material';
import { DarkUpperSection, PageSection } from '../../components';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ManifestManagement from './components/ManifestManagement';
import SSCCManagement from './components/SSCCManagement';
import ReportingLogs from './components/ReportingLogs';
import { useCurrentUser } from '../../hooks';

const ADMIN_PANEL_ALLOWED_EMAILS = ['george.burke@mrl.com.au'];
const isProd = process.env.REACT_APP_IS_PROD === 'true';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`admin-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminPanel() {
  const theme = useTheme();
  const user = useCurrentUser();
  const [currentTab, setCurrentTab] = useState(0);

  if (isProd && !ADMIN_PANEL_ALLOWED_EMAILS.includes(user.email?.toLowerCase())) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AdminPanelSettingsIcon sx={{ fontSize: 32, color: 'primary.contrastText' }} />
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Admin Panel
            </Typography>
          </Stack>
          <Typography variant="body1" color={theme.palette.primary.contrastText}>
            Manage manifests, SSCCs, and view system logs
          </Typography>
        </Stack>
      </DarkUpperSection>
      <PageSection>
        <Box sx={{ width: '100%', marginTop: '2em' }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'white',
              borderRadius: '8px 8px 0 0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="admin panel tabs"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }
              }}>
              <Tab icon={<LocalShippingIcon />} iconPosition="start" label="Manifest Management" sx={{ gap: 1 }} />
              <Tab icon={<InventoryIcon />} iconPosition="start" label="SSCC Management" sx={{ gap: 1 }} />
              <Tab icon={<AssessmentIcon />} iconPosition="start" label="Reporting & Logs" sx={{ gap: 1 }} />
            </Tabs>
          </Box>

          <Box
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              minHeight: '60vh'
            }}>
            <TabPanel value={currentTab} index={0}>
              <ManifestManagement />
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              <SSCCManagement />
            </TabPanel>
            <TabPanel value={currentTab} index={2}>
              <ReportingLogs />
            </TabPanel>
          </Box>
        </Box>
      </PageSection>
    </>
  );
}
